import { motion } from "framer-motion";
import { PiXBold } from "react-icons/pi";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import Proximiio from "proximiio-js-library";
import {
  osName,
  browserName,
  mobileModel,
  mobileVendor,
  deviceType,
} from "react-device-detect";
import { Button } from "./ui/button";
import {
  FilterCategoryModel,
  FilterItemModel,
} from "@/models/filterItem.model";

import useMapStore from "@/store/mapStore";
import { t } from "i18next";
import { cn, filterByAmenity, getSortedPOIs } from "@/lib/utils";
import useFeaturesStore from "@/store/featuresStore";
import { IoLocationOutline } from "react-icons/io5";
import { useMemo, useState } from "react";
import { SortedPoiItemModel } from "@/models/sortedPoiItem.model";

interface FeatureGroup extends SortedPoiItemModel {
  groupedItems: SortedPoiItemModel[];
  groupCount: number;
  isGroup: boolean;
}

function PoiList() {
  const activeFilter = useMapStore((state) => state.activeFilter);
  const features = useFeaturesStore((state) => state.features);
  const filterCategories = useMapStore((state) => state.filterCategories);
  const currentLang = useMapStore((state) => state.currentLang);
  const floors = useMapStore((state) => state.floors);
  const activeFilterCategory = filterCategories.find((category) =>
    category.items.find((items) => items.id === activeFilter.id),
  );
  const mobileMode = useMapStore((state) => state.mobileMode);
  const kioskMode = useMapStore((state) => state.kioskMode);
  const activeKiosk = useMapStore((state) => state.activeKiosk);
  const appSession = useMapStore((state) => state.appSession);
  const map = useMapStore((state) => state.map);
  const amenities = useMapStore((state) => state.amenities);
  const verticalKioskMode = useMapStore((state) => state.verticalKioskMode);
  const kioskScaling = useMapStore((state) => state.kioskScaling);

  const setRouteFinish = useMapStore((state) => state.setRouteFinish);
  const centerToFinish = useMapStore((state) => state.centerToFinish);
  const setActiveFilter = useMapStore((state) => state.setActiveFilter);
  const setIsLoading = useMapStore((state) => state.setIsLoading);
  const setActiveCategory = useMapStore((state) => state.setActiveCategory);

  const pois = useMemo(() => {
    return getSortedPOIs({ features, currentLang, floors });
  }, [features, currentLang, floors]);
  const [selectedGroup, setSelectedGroup] = useState({} as FeatureGroup);

  const filteredPois = useMemo(() => {
    // Step 1: Filter by type
    const poisFiltered = pois.filter(
      (feature) => feature.properties.type === "poi",
    );

    // Step 2: Apply your existing filter
    const filtered = filterByAmenity(poisFiltered, activeFilter.id);

    // Step 3: Sort by title then level
    const sorted = filtered.sort((a, b) => {
      if (a.properties.title === b.properties.title) {
        return a.properties.level - b.properties.level;
      } else {
        return a.properties.title.localeCompare(b.properties.title);
      }
    });

    // Step 4: Group if needed
    if (activeFilter.groupResults) {
      const groupedMap = new Map();

      for (const poi of sorted) {
        // Normalize title for grouping (trim + lowercase)
        const rawTitle = poi?.properties?.title ?? "";
        const normalizedTitle = rawTitle.trim().toLowerCase();

        if (!groupedMap.has(normalizedTitle)) {
          groupedMap.set(normalizedTitle, []);
        }
        groupedMap.get(normalizedTitle).push(poi);
      }

      // Step 5: Build grouped result array
      const groupedResults = Array.from(groupedMap.entries()).map(
        ([normalizedTitle, items]) => {
          const representative = items[0];
          const title = representative.properties?.title ?? normalizedTitle;

          if (items.length > 1) {
            return {
              ...representative,
              properties: {
                ...representative.properties,
                title,
              },
              isGroup: true,
              groupCount: items.length,
              groupedItems: items, // optional: originals
            };
          } else {
            return items[0];
          }
        },
      );

      // Step 6: Sort grouped results by original title
      groupedResults.sort((a, b) => {
        // Strip counts like " (3)" from the comparison
        const titleA = a.properties.title.replace(/\s\(\d+\)$/, "");
        const titleB = b.properties.title.replace(/\s\(\d+\)$/, "");
        return titleA.localeCompare(titleB);
      });

      return groupedResults;
    }

    // Step 7: If not grouping, return sorted as before
    return sorted;
  }, [activeFilter, pois]);

  const onSelectHandle = (featureId: string) => {
    //setIsLoading(true);
    setTimeout(() => {
      const feature = features.find((feature) => feature.id === featureId)!;
      setRouteFinish(feature);
      centerToFinish();

      if (
        feature.properties?.metadata?.prevent_polygon === true ||
        feature.properties?.metadata?.prevent_polygon === "true"
      ) {
        setActiveFilter({} as FilterItemModel);
        setTimeout(() => {
          map.disablePolygons();
        }, 1000);
      }

      //save feature select log
      console.log("LOG: save feature select log");
      const userData = {
        osName,
        browserName,
        mobileModel,
        mobileVendor,
        deviceType:
          kioskMode && !mobileMode && activeKiosk?.name
            ? activeKiosk?.name
            : deviceType,
      };

      new Proximiio.InteractionLogger({
        interactionType: "select",
        targetElementType: "feature",
        targetElementId: feature ? feature.id : undefined,
        targetElementTitle: feature ? feature.properties.title : undefined,
        targetElementAmenity: t(activeFilter.title, { lng: "en" }),
        targetElementAmenityCategory: activeFilterCategory
          ? t(activeFilterCategory?.title, { lng: "en" })
          : undefined,
        kioskId: activeKiosk?.id ? activeKiosk?.id : undefined,
        kioskTitle: activeKiosk?.name ? activeKiosk?.name : undefined,
        metadata: userData,
        language: currentLang,
        session: appSession,
      });
    }, 100);
  };

  const onGroupSelect = (feature: FeatureGroup) => {
    map.setFeatureFilter(feature.properties.title);
    setSelectedGroup(feature);
  };

  const onCloseHandle = () => {
    if (selectedGroup.groupedItems) {
      map.removeFeatureFilter(selectedGroup.properties.title);
      setSelectedGroup({} as FeatureGroup);
      return;
    }
    if (Object.keys(map).length > 0) {
      if (mobileMode) {
        setActiveFilter({} as FilterItemModel);
        map.enablePolygonPreventedIcons();
        setTimeout(() => {
          map.setFeaturesHighlight([]);
        }, 1000);
        return;
      }
      setIsLoading(true);
      if (activeFilterCategory?.items.length === 1) {
        setActiveCategory({} as FilterCategoryModel);
      }
      setTimeout(() => {
        console.log("close handler");
        setActiveFilter({} as FilterItemModel);
        map.enablePolygonPreventedIcons();
      }, 100);
      setTimeout(() => {
        map.setFeaturesHighlight([]);
      }, 1000);
    }
  };

  const listItem = (
    feature: SortedPoiItemModel | FeatureGroup,
    idx: number,
  ) => {
    const amenity = amenities.find((a) => a.id === feature.properties.amenity);
    if (import.meta.env.VITE_PROXIMIIO_BUNDLE_URL && amenity) {
      amenity.icon = `${import.meta.env.VITE_PROXIMIIO_BUNDLE_URL}/amenities/${amenity.id}.png`;
    }

    return (
      <motion.li
        key={feature.properties.id}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          type: "spring",
          stiffness: 260,
          damping: 20,
          delay: 0.1 + idx / 10,
        }}
        className={cn(
          "group flex cursor-pointer items-center justify-between gap-2 py-1 font-medium hover:text-primary kiosk:py-2",
          activeFilterCategory?.color && "hover:text-[var(--category-color)]",
          activeFilter.color && "hover:text-[var(--item-color)]",
        )}
        style={{
          // @ts-expect-error: custom property
          "--item-color": activeFilter.color,
        }}
        onClick={() => {
          if ((feature as FeatureGroup).isGroup) {
            onGroupSelect(feature as FeatureGroup);
          } else {
            onSelectHandle(feature.id);
          }
        }}
      >
        {amenity && (
          <img
            src={amenity.icon}
            alt={feature.properties.title}
            className="max-h-5 max-w-5 lg:max-h-8 lg:max-w-8 kiosk:max-h-10 kiosk:max-w-10"
          />
        )}
        <span className="flex flex-1 items-center gap-2 kiosk:text-xl">
          <span>{feature.properties.title}</span>
          {(feature as FeatureGroup).isGroup && (
            <span className="flex">
              (<IoLocationOutline className="text-xl" />
              {(feature as FeatureGroup).groupCount})
            </span>
          )}
        </span>
      </motion.li>
    );
  };

  return (
    <>
      <motion.div
        key="list"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0 }}
        transition={{ duration: 0.2 }}
        className={cn(
          mobileMode || verticalKioskMode
            ? "absolute top-[calc(100dvh-384px)] z-[101] h-96 w-full rounded-t-2xl bg-white p-6 shadow-[0px_1px_6px_3px_rgba(0,0,0,0.5)] kiosk:bottom-12 kiosk:left-[calc(5dvw)] kiosk:top-auto kiosk:h-auto kiosk:w-[calc(90dvw)] kiosk:rounded-2xl"
            : "flex h-full flex-col kiosk:absolute kiosk:bottom-10 kiosk:mx-9 kiosk:h-1/5 kiosk:w-[460px] kiosk:rounded-2xl kiosk:bg-white kiosk:py-5 kiosk:shadow-[0px_1px_6px_3px_rgba(0,0,0,0.5)]",
          !kioskScaling &&
            "kiosk:bottom-10 kiosk:left-[2dvw] kiosk:w-[calc(64dvw)] kiosk:origin-bottom-left kiosk:scale-150 kiosk:!transform-cpu",
        )}
        style={{
          // @ts-expect-error: custom property
          "--category-color": `hsl(${activeFilterCategory?.color})`,
          "--custom-handle-bg": mobileMode
            ? `var(--primary)`
            : `${activeFilterCategory?.color}`,
        }}
      >
        <div
          className={cn("mb-4 flex items-center px-10", mobileMode && "px-0")}
        >
          <h1 className="flex-1 text-2xl font-bold capitalize">
            {selectedGroup.groupedItems
              ? selectedGroup.properties.title
              : t(activeFilter.title)}
            {activeFilter.listAsClosest && currentLang === "en" ? "s" : ""}
          </h1>
          <Button
            onClick={onCloseHandle}
            size="icon"
            variant="outline"
            className={cn(
              "-mr-2 h-12 w-12 justify-end border-0 bg-transparent text-4xl",
              mobileMode &&
                "mr-0 h-8 w-8 justify-center bg-primary text-lg text-white hover:bg-primary/70",
              activeFilterCategory?.color &&
                !mobileMode &&
                "hover:text-[var(--category-color)]",
              activeFilterCategory?.color &&
                mobileMode &&
                "hover:text-white hover:opacity-70",
            )}
          >
            <PiXBold />
          </Button>
        </div>
        <OverlayScrollbarsComponent
          className={cn(
            "overflow-auto",
            mobileMode || verticalKioskMode
              ? "max-h-72 px-0 pr-10 kiosk:h-[525px] kiosk:max-h-none"
              : "px-10 pb-5",
            mobileMode ? "max-h-72 px-0 pr-10" : "px-10 pb-5",
          )}
          options={{
            scrollbars: { autoHide: "move", autoHideDelay: 3000 },
            overflow: { x: "hidden" },
          }}
          defer
        >
          <ul>
            {!selectedGroup.groupedItems &&
              filteredPois.map((feature, idx) => {
                return listItem(feature, idx);
              })}
            {selectedGroup.groupedItems &&
              selectedGroup.groupedItems.map((feature, idx) => {
                return listItem(feature, idx);
              })}
          </ul>
        </OverlayScrollbarsComponent>
      </motion.div>
    </>
  );
}

export default PoiList;
